import React, { useEffect, useRef } from 'react';

/**
 * Fills the stage when the candidate is sharing their screen.
 *
 * Sharing the *entire* screen back into a tab that itself shows the share creates an infinite
 * "hall of mirrors". `hideMirror` (the eye toggle in the controls) swaps the live video for a calm
 * placeholder to avoid that distraction — the recruiter still receives the real stream; only the
 * candidate's own view is hidden.
 */
const ScreenShareView = ({ stream, hideMirror = true }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-xl bg-slate-200/60 dark:bg-black/40">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`h-full w-full object-contain ${hideMirror ? 'hidden' : ''}`}
      />
      {hideMirror && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-200/70 ring-1 ring-black/5 dark:bg-white/5 dark:ring-white/10">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#a78bfa" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4M12 8v4M9 11l3-3 3 3" />
            </svg>
          </span>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Your screen is being shared</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Mirroring hidden to avoid distraction</p>
        </div>
      )}
      <span className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md bg-emerald-100 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/20">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse dark:bg-emerald-400" /> Sharing screen
      </span>
    </div>
  );
};

export default ScreenShareView;
